import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  BookOpen,
  FileText,
  Download,
  Upload,
  History,
  Archive,
  Send,
  RotateCcw,
  Lock,
  Users,
  Monitor,
  Package,
  Wifi,
  Cloud,
  Database,
  AlertTriangle,
  GitBranch,
  Activity,
  Shield,
  X,
  ChevronLeft,
  ChevronRight,
  Paperclip,
  Clock
} from 'lucide-react';
import { apiService } from '../services/api';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../contexts/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/quill-custom.css';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';
import { sanitizeHtml } from '../utils/sanitizeHtml';

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  sort_order: number;
  procedure_count: number;
}

interface Attachment {
  id: number;
  procedure_id: number;
  file_name: string;
  original_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  is_main_document: boolean;
  uploaded_by: number;
  uploaded_by_name: string;
  uploaded_at: string;
}

interface Procedure {
  id: number;
  procedure_code: string;
  category_id: number;
  title: string;
  content: string;
  version: string;
  status: 'draft' | 'published' | 'archived';
  is_active: boolean;
  created_by: number;
  updated_by: number;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  published_by: number | null;
  category_name: string;
  category_icon: string;
  category_color: string;
  created_by_name: string;
  updated_by_name: string;
  published_by_name?: string;
  attachment_count: number;
  attachments?: Attachment[];
}

interface HistoryRecord {
  id: number;
  procedure_id: number;
  action_type: 'create' | 'update' | 'publish' | 'archive' | 'restore';
  version_before: string | null;
  version_after: string | null;
  changes_summary: string;
  old_values: any;
  new_values: any;
  performed_by: number;
  performed_by_name: string;
  performed_at: string;
}

// Icon mapping
const iconMap: { [key: string]: React.ComponentType<{ className?: string }> } = {
  'lock': Lock,
  'users': Users,
  'monitor': Monitor,
  'package': Package,
  'wifi': Wifi,
  'cloud': Cloud,
  'database': Database,
  'alert-triangle': AlertTriangle,
  'git-branch': GitBranch,
  'activity': Activity,
  'shield': Shield,
  'file-text': FileText
};

// React Quill configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['blockquote', 'code-block'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent',
  'align', 'link', 'image', 'blockquote', 'code-block'
];

const ProceduresPage: React.FC = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { showNotification } = useNotifications();

  // Data states
  const [categories, setCategories] = useState<Category[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingProcedure, setEditingProcedure] = useState<Procedure | null>(null);
  const [history, setHistory] = useState<HistoryRecord[]>([]);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    category_id: null as number | null,
    content: '',
    status: 'draft' as 'draft' | 'published'
  });
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 10;

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const canCreate = ['admin', 'superadmin', 'teknisyen'].includes(user?.role || '');

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await apiService.get('/procedures/categories');
      setCategories(response?.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      showNotification(language === 'en' ? 'Error loading categories' : 'Kategoriler yüklenirken hata oluştu', 'error');
    }
  };

  // Fetch procedures
  const fetchProcedures = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        search: searchTerm,
        ...(selectedCategory && { category: selectedCategory.toString() }),
        ...(statusFilter && { status: statusFilter })
      });

      const response = await apiService.get(`/procedures?${params}`);
      setProcedures(response?.data || []);
      setTotalPages(response?.pagination?.totalPages || 1);
      setTotal(response?.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching procedures:', error);
      showNotification(t('procedures.errLoad'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch procedure detail
  const fetchProcedureDetail = async (id: number) => {
    try {
      const response = await apiService.get(`/procedures/${id}`);
      return response?.data;
    } catch (error) {
      console.error('Error fetching procedure detail:', error);
      showNotification(language === 'en' ? 'Error loading procedure detail' : 'Prosedür detayı yüklenirken hata oluştu', 'error');
      return null;
    }
  };

  // Fetch history
  const fetchHistory = async (id: number) => {
    try {
      const response = await apiService.get(`/procedures/${id}/history`);
      setHistory(response?.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      showNotification(language === 'en' ? 'Error loading history' : 'Geçmiş yüklenirken hata oluştu', 'error');
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProcedures();
  }, [currentPage, searchTerm, selectedCategory, statusFilter]);

  const resetForm = () => {
    setFormData({
      title: '',
      category_id: selectedCategory,
      content: '',
      status: 'draft'
    });
    setPendingFiles([]);
    setEditingProcedure(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      showNotification(language === 'en' ? 'Title is required' : 'Başlık zorunludur', 'error');
      return;
    }

    if (!formData.category_id) {
      showNotification(language === 'en' ? 'Category selection is required' : 'Kategori seçimi zorunludur', 'error');
      return;
    }

    try {
      setSaving(true);

      let procedureId: number;

      if (editingProcedure) {
        await apiService.put(`/procedures/${editingProcedure.id}`, formData);
        procedureId = editingProcedure.id;
        showNotification(t('procedures.successUpdated'), 'success');
      } else {
        const response = await apiService.post('/procedures', formData);
        procedureId = response.data.id;
        showNotification(t('procedures.successCreated'), 'success');
      }

      // Upload pending files
      if (pendingFiles.length > 0) {
        const fileFormData = new FormData();
        pendingFiles.forEach(file => {
          fileFormData.append('files', file);
        });

        try {
          await apiService.upload(`/procedures/${procedureId}/attachments`, fileFormData);
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          showNotification(language === 'en' ? 'Error uploading files' : 'Dosyalar yüklenirken hata oluştu', 'warning');
        }
      }

      setShowModal(false);
      resetForm();
      fetchProcedures();
      fetchCategories();
    } catch (error: any) {
      console.error('Error saving procedure:', error);
      showNotification(
        error.response?.data?.error || t('procedures.errSave'),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (procedure: Procedure) => {
    const detail = await fetchProcedureDetail(procedure.id);
    if (detail) {
      setEditingProcedure(detail);
      setFormData({
        title: detail.title,
        category_id: detail.category_id,
        content: detail.content || '',
        status: detail.status === 'archived' ? 'draft' : detail.status
      });
      setPendingFiles([]);
      setShowModal(true);
    }
  };

  const handleView = async (procedure: Procedure) => {
    const detail = await fetchProcedureDetail(procedure.id);
    if (detail) {
      setSelectedProcedure(detail);
      setShowViewModal(true);
    }
  };

  const handleViewHistory = async (procedure: Procedure) => {
    await fetchHistory(procedure.id);
    setSelectedProcedure(procedure);
    setShowHistoryModal(true);
  };

  const handlePublish = async (procedure: Procedure) => {
    if (!window.confirm(language === 'en' ? `Are you sure you want to publish "${procedure.title}"?` : `"${procedure.title}" prosedürünü yayınlamak istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await apiService.post(`/procedures/${procedure.id}/publish`, {});
      showNotification(t('procedures.successPublished'), 'success');
      fetchProcedures();
      fetchCategories();
    } catch (error: any) {
      console.error('Error publishing procedure:', error);
      showNotification(
        error.response?.data?.error || (language === 'en' ? 'Error publishing procedure' : 'Prosedür yayınlanırken hata oluştu'),
        'error'
      );
    }
  };

  const handleArchive = async (procedure: Procedure) => {
    if (!window.confirm(language === 'en' ? `Are you sure you want to archive "${procedure.title}"?` : `"${procedure.title}" prosedürünü arşivlemek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await apiService.post(`/procedures/${procedure.id}/archive`, {});
      showNotification(t('procedures.successArchived'), 'success');
      fetchProcedures();
      fetchCategories();
    } catch (error: any) {
      console.error('Error archiving procedure:', error);
      showNotification(
        error.response?.data?.error || (language === 'en' ? 'Error archiving procedure' : 'Prosedür arşivlenirken hata oluştu'),
        'error'
      );
    }
  };

  const handleRestore = async (procedure: Procedure) => {
    try {
      await apiService.post(`/procedures/${procedure.id}/restore`, {});
      showNotification(t('procedures.successRestored'), 'success');
      fetchProcedures();
      fetchCategories();
    } catch (error: any) {
      console.error('Error restoring procedure:', error);
      showNotification(
        error.response?.data?.error || (language === 'en' ? 'Error restoring procedure' : 'Prosedür geri yüklenirken hata oluştu'),
        'error'
      );
    }
  };

  const handleDelete = async (procedure: Procedure) => {
    if (!window.confirm(language === 'en' ? `Are you sure you want to delete "${procedure.title}"?` : `"${procedure.title}" prosedürünü silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await apiService.delete(`/procedures/${procedure.id}`);
      showNotification(t('procedures.successDeleted'), 'success');
      fetchProcedures();
      fetchCategories();
    } catch (error: any) {
      console.error('Error deleting procedure:', error);
      showNotification(
        error.response?.data?.error || (language === 'en' ? 'Error deleting procedure' : 'Prosedür silinirken hata oluştu'),
        'error'
      );
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!window.confirm(t('procedures.confirmDeleteFile'))) {
      return;
    }

    try {
      await apiService.delete(`/procedures/attachments/${attachmentId}`);
      showNotification(t('procedures.successFileDeleted'), 'success');
      // Refresh the view modal
      if (selectedProcedure) {
        const detail = await fetchProcedureDetail(selectedProcedure.id);
        if (detail) {
          setSelectedProcedure(detail);
        }
      }
    } catch (error: any) {
      console.error('Error deleting attachment:', error);
      showNotification(
        error.response?.data?.error || (language === 'en' ? 'Error deleting file' : 'Dosya silinirken hata oluştu'),
        'error'
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      if (!validTypes.includes(file.type)) {
        showNotification(`${file.name}: ${language === 'en' ? 'Only PDF and DOCX files can be uploaded' : 'Sadece PDF ve DOCX dosyaları yüklenebilir'}`, 'error');
        return false;
      }
      if (file.size > 20 * 1024 * 1024) {
        showNotification(`${file.name}: ${language === 'en' ? 'File size cannot exceed 20MB' : "Dosya boyutu 20MB'dan büyük olamaz"}`, 'error');
        return false;
      }
      return true;
    });
    setPendingFiles(prev => [...prev, ...validFiles]);
  };

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
            <span className="h-1.5 w-1.5 rounded-full mr-1.5 bg-green-600"></span>
            {t('procedures.statusPublished')}
          </span>
        );
      case 'draft':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
            <span className="h-1.5 w-1.5 rounded-full mr-1.5 bg-yellow-600"></span>
            {language === 'en' ? 'Draft' : 'Taslak'}
          </span>
        );
      case 'archived':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200">
            <span className="h-1.5 w-1.5 rounded-full mr-1.5 bg-gray-600"></span>
            {t('procedures.statusArchived')}
          </span>
        );
      default:
        return null;
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'create':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'update':
        return <Edit className="h-4 w-4 text-primary" />;
      case 'publish':
        return <Send className="h-4 w-4 text-green-600" />;
      case 'archive':
        return <Archive className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
      case 'restore':
        return <RotateCcw className="h-4 w-4 text-primary" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getActionLabel = (actionType: string) => {
    switch (actionType) {
      case 'create':
        return t('procedures.historyCreated');
      case 'update':
        return t('procedures.historyUpdated');
      case 'publish':
        return t('procedures.historyPublished');
      case 'archive':
        return t('procedures.historyArchived');
      case 'restore':
        return t('procedures.historyRestored');
      default:
        return actionType;
    }
  };

  const getCategoryIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName] || FileText;
    return IconComponent;
  };

  if (loading && procedures.length === 0 && categories.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center space-x-3">
          <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 dark:text-white">
              {t('procedures.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 text-sm sm:text-base">
              {t('procedures.subtitle')}
            </p>
          </div>
        </div>
        {canCreate && (
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-primary hover:bg-primary-dark text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            <span>{t('procedures.newProcedure')}</span>
          </button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Categories Sidebar */}
        <div className="lg:w-72 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 dark:text-white mb-4">{language === 'en' ? 'Categories' : 'Kategoriler'}</h3>
            <div className="space-y-1">
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setCurrentPage(1);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                  selectedCategory === null
                    ? 'bg-primary/20 text-primary-dark dark:bg-primary/30 dark:text-accent'
                    : 'hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm">{t('procedures.statusAll')}</span>
                </div>
                <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                  {categories.reduce((sum, c) => sum + c.procedure_count, 0)}
                </span>
              </button>
              {categories.map(category => {
                const IconComponent = getCategoryIcon(category.icon);
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setCurrentPage(1);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-primary/20 text-primary-dark dark:bg-primary/30 dark:text-accent'
                        : 'hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <IconComponent className="h-4 w-4" style={{ color: category.color }} />
                      <span className="text-sm truncate">{category.name}</span>
                    </div>
                    <span className="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                      {category.procedure_count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="sm:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder={t('procedures.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white min-h-[44px]"
                  />
                </div>
              </div>
              {canCreate && (
                <div>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white min-h-[44px]"
                  >
                    <option value="">{t('procedures.allStatuses')}</option>
                    <option value="draft">{language === 'en' ? 'Draft' : 'Taslak'}</option>
                    <option value="published">{t('procedures.statusPublished')}</option>
                    <option value="archived">{t('procedures.statusArchived')}</option>
                  </select>
                </div>
              )}
              <div className="flex items-center justify-end">
                <Filter className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300">
                  {total} {language === 'en' ? 'procedure' : 'prosedür'}
                </span>
              </div>
            </div>
          </div>

          {/* Procedures List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : procedures.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {procedures.map((procedure) => {
                  const CategoryIcon = getCategoryIcon(procedure.category_icon);
                  return (
                    <div
                      key={procedure.id}
                      className="p-4 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1 min-w-0">
                          <div
                            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 flex-shrink-0"
                            style={{ color: procedure.category_color }}
                          >
                            <CategoryIcon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => handleView(procedure)}
                                className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white hover:text-primary transition-colors truncate"
                              >
                                {procedure.procedure_code}: {procedure.title}
                              </button>
                              {getStatusBadge(procedure.status)}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                              <span>v{procedure.version}</span>
                              <span>{procedure.category_name}</span>
                              {procedure.attachment_count > 0 && (
                                <span className="flex items-center">
                                  <Paperclip className="h-3 w-3 mr-1" />
                                  {procedure.attachment_count} {language === 'en' ? 'file' : 'dosya'}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {language === 'en' ? 'Last:' : 'Son:'} {procedure.updated_by_name} - {new Date(procedure.updated_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={() => handleView(procedure)}
                            className="p-2 text-gray-400 hover:text-primary transition-colors"
                            title={language === 'en' ? 'View' : 'Görüntüle'}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {canCreate && (
                            <>
                              <button
                                onClick={() => handleViewHistory(procedure)}
                                className="p-2 text-gray-400 hover:text-purple-600 transition-colors"
                                title={language === 'en' ? 'History' : 'Geçmiş'}
                              >
                                <History className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(procedure)}
                                className="p-2 text-gray-400 hover:text-amber-600 transition-colors"
                                title={language === 'en' ? 'Edit' : 'Düzenle'}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {isAdmin && (
                            <>
                              {procedure.status === 'draft' && (
                                <button
                                  onClick={() => handlePublish(procedure)}
                                  className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                                  title={language === 'en' ? 'Publish' : 'Yayınla'}
                                >
                                  <Send className="h-4 w-4" />
                                </button>
                              )}
                              {procedure.status === 'published' && (
                                <button
                                  onClick={() => handleArchive(procedure)}
                                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
                                  title={language === 'en' ? 'Archive' : 'Arşivle'}
                                >
                                  <Archive className="h-4 w-4" />
                                </button>
                              )}
                              {procedure.status === 'archived' && (
                                <button
                                  onClick={() => handleRestore(procedure)}
                                  className="p-2 text-gray-400 hover:text-primary transition-colors"
                                  title={language === 'en' ? 'Restore' : 'Geri Yükle'}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(procedure)}
                                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                title={language === 'en' ? 'Delete' : 'Sil'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                {selectedCategory
                  ? t('procedures.notFoundCategory')
                  : t('procedures.notFound')}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 rounded-md ${
                          currentPage === page
                            ? 'bg-primary text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                  {editingProcedure ? t('procedures.editTitle') : t('procedures.newTitle')}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Title *' : 'Başlık *'}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    placeholder={t('procedures.titlePlaceholder')}
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Category *' : 'Kategori *'}
                  </label>
                  <select
                    required
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData({ ...formData, category_id: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('procedures.selectCategory')}</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Content' : 'İçerik'}
                  </label>
                  <div className="bg-white dark:bg-gray-800 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                    <ReactQuill
                      value={formData.content}
                      onChange={(value) => setFormData({ ...formData, content: value })}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder={t('procedures.contentPlaceholder')}
                      style={{ minHeight: '200px' }}
                      theme="snow"
                    />
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'File Attachments (PDF, DOCX)' : 'Dosya Ekleri (PDF, DOCX)'}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {language === 'en' ? 'Click to select file' : 'Dosya seçmek için tıklayın'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400 mt-1">
                        PDF veya DOCX (Max 20MB)
                      </span>
                    </label>
                  </div>

                  {/* Pending Files */}
                  {pendingFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {pendingFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-2"
                        >
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                              {file.name}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({formatFileSize(file.size)})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removePendingFile(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Existing Attachments */}
                  {editingProcedure?.attachments && editingProcedure.attachments.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{language === 'en' ? 'Existing Files:' : 'Mevcut Dosyalar:'}</p>
                      <div className="space-y-2">
                        {editingProcedure.attachments.map((att) => (
                          <div
                            key={att.id}
                            className="flex items-center justify-between bg-primary/10 dark:bg-primary/20 rounded-lg p-2"
                          >
                            <div className="flex items-center space-x-2">
                              <FileText className="h-4 w-4 text-primary" />
                              <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                                {att.original_name}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({formatFileSize(att.file_size)})
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Status (only for admins) */}
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'en' ? 'Status' : 'Durum'}
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as 'draft' | 'published' })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    >
                      <option value="draft">{language === 'en' ? 'Draft' : 'Taslak'}</option>
                      <option value="published">{t('procedures.statusPublish')}</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="p-6 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  {t('procedures.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  {saving ? t('procedures.saving') : editingProcedure ? t('procedures.update') : t('procedures.save')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* View Modal */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)}>
          {selectedProcedure && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700"
                  style={{ color: selectedProcedure.category_color }}
                >
                  {React.createElement(getCategoryIcon(selectedProcedure.category_icon), { className: 'h-5 w-5' })}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                      {selectedProcedure.procedure_code}: {selectedProcedure.title}
                    </h2>
                    {getStatusBadge(selectedProcedure.status)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    v{selectedProcedure.version} | {selectedProcedure.category_name} | {language === 'en' ? 'Last:' : 'Son:'} {selectedProcedure.updated_by_name}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 prose dark:prose-invert max-w-none">
              {selectedProcedure.content ? (
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedProcedure.content) }} />
              ) : (
                <p className="text-gray-500 dark:text-gray-400 italic">{t('procedures.noContent')}</p>
              )}
            </div>

            {/* Attachments */}
            {selectedProcedure.attachments && selectedProcedure.attachments.length > 0 && (
              <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white mb-3">
                  {language === 'en' ? 'Attachments' : 'Ekli Dosyalar'} ({selectedProcedure.attachments.length})
                </h3>
                <div className="space-y-2">
                  {selectedProcedure.attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-3"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white">
                            {att.original_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatFileSize(att.file_size)} | {att.uploaded_by_name} | {new Date(att.uploaded_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {att.file_type === 'application/pdf' && (
                          <a
                            href={`/api/procedures/attachments/${att.id}/view`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-primary transition-colors"
                            title={language === 'en' ? 'View' : 'Görüntüle'}
                          >
                            <Eye className="h-4 w-4" />
                          </a>
                        )}
                        <a
                          href={`/api/procedures/attachments/${att.id}/download`}
                          className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                          title={language === 'en' ? 'Download' : 'İndir'}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteAttachment(att.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title={language === 'en' ? 'Delete' : 'Sil'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center rounded-b-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {language === 'en' ? 'Created by:' : 'Oluşturan:'} {selectedProcedure.created_by_name} | {new Date(selectedProcedure.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                {selectedProcedure.published_at && (
                  <> | {language === 'en' ? 'Published by:' : 'Yayınlayan:'} {selectedProcedure.published_by_name} | {new Date(selectedProcedure.published_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</>
                )}
              </div>
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                {language === 'en' ? 'Close' : 'Kapat'}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedProcedure && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 dark:text-white">
                  {language === 'en' ? 'Procedure History' : 'Prosedür Geçmişi'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedProcedure.procedure_code}: {selectedProcedure.title}
                </p>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {history.length > 0 ? (
                <div className="space-y-4">
                  {history.map((record) => (
                    <div
                      key={record.id}
                      className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="p-2 rounded-full bg-white dark:bg-gray-800 dark:bg-gray-600">
                        {getActionIcon(record.action_type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 dark:text-white">
                            {getActionLabel(record.action_type)}
                          </p>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(record.performed_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 mt-1">
                          {record.changes_summary}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{record.performed_by_name}</span>
                          {record.version_before && record.version_after && (
                            <>
                              <span>|</span>
                              <span>v{record.version_before} → v{record.version_after}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {language === 'en' ? 'No history found.' : 'Geçmiş kaydı bulunamadı.'}
                </p>
              )}
            </div>

            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-end rounded-b-lg">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                {language === 'en' ? 'Close' : 'Kapat'}
              </button>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default ProceduresPage;
